import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Eye,
  Pencil,
  PowerOff,
  Building2,
  Search,
  UserPlus,
} from "lucide-react";
import { api } from "@/lib/api";
import { TramitadorPicker } from "@/components/TramitadorPicker";
import { StatusBadge, formatDate } from "@/components/DetailLayout";
import { CambiarEstatusModal } from "@/components/CambiarEstatusModal";

/**
 * Relación Titular ↔ Tramitador ↔ Personas (requerimiento «Modelo de relación
 * Titular – Tramitador – Usuarios Autorizados», 2026-08-15).
 *
 * El mismo panel sirve en los dos sentidos porque la relación es una sola; lo
 * que cambia es desde dónde se mira:
 *  - `perspectiva="TRAMITADOR"` → §8, «Empresas para las que esta razón social
 *    está autorizada».
 *  - `perspectiva="TITULAR"` → §9, «Tramitadores autorizados» de esta empresa.
 *
 * Ningún dato se deriva de un rol global de la persona: las personas listadas
 * son las autorizadas EN ESA CARTA, así que la misma cuenta puede aparecer en
 * unas relaciones y no en otras sin perder ningún otro papel (§3).
 */

export interface PersonaAutorizada {
  asociacionId: string;
  usuarioId: string;
  nombreCompleto: string;
  correo: string | null;
  tipoAutorizacion: string;
  puestoArea: string | null;
  sectoresCodigos: string[];
  vigenciaInicio: string;
  vigenciaFin: string | null;
  estatus: string;
  motivoCambio: string | null;
}

export interface RelacionTramitacion {
  cartaPoderId: string;
  titularId: string;
  titularRazonSocial: string;
  titularRfc: string;
  titularEstatus: string;
  tramitadorId: string;
  tramitadorRazonSocial: string;
  tramitadorRfc: string;
  tramitadorEstatus: string;
  representantesContraparte: { id: string; nombreCompleto: string; correo: string | null }[];
  documentoId: string | null;
  documentoNombre: string | null;
  esCopiaCertificadaOriginal: boolean;
  vigenciaInicio: string;
  vigenciaFin: string | null;
  sinVencimiento: boolean;
  estatusCarta: string;
  cartaVigente: boolean;
  /** La relación opera hoy: carta vigente y ambas razones sociales en operación. */
  relacionVigente: boolean;
  motivoNoVigente: string | null;
  totalPersonasActivas: number;
  personas: PersonaAutorizada[];
}

export interface RelacionesTramitacion {
  comoTitular: RelacionTramitacion[];
  comoTramitador: RelacionTramitacion[];
}

interface SectorOption {
  id: string;
  codigo: string;
  nombre: string;
}

const TIPO_AUTORIZACION_LABEL: Record<string, string> = {
  INTERNO_TITULAR: "Interno del titular",
  CONSULTA: "Consulta",
  GESTION: "Gestión",
  SEGUIMIENTO: "Seguimiento",
  FIRMA: "Firma",
};
const ESTATUS_PERSONA_LABEL: Record<string, string> = {
  ACTIVA: "Activa",
  SUSPENDIDA: "Inactiva",
  REVOCADA: "Revocada",
  VENCIDA: "Vencida",
  SUSTITUIDA: "Sustituida",
  PENDIENTE: "Pendiente",
};

function vigenciaTexto(r: RelacionTramitacion): string {
  if (r.sinVencimiento) return "Sin vencimiento";
  return formatDate(r.vigenciaFin) ?? "No especificada";
}

function errorDetalle(e: unknown): string {
  const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
  return data?.detail || data?.title || (e as Error).message;
}

export function RelacionesTramitacionPanel({
  relaciones,
  perspectiva,
  puedeAdministrar,
  titularId,
  titularRazonSocial,
  onVerDocumento,
  onCambio,
}: {
  relaciones: RelacionTramitacion[];
  perspectiva: "TITULAR" | "TRAMITADOR";
  puedeAdministrar: boolean;
  /**
   * Razón social dueña de la pantalla, para poder otorgar cartas nuevas desde
   * aquí. Solo se pasa en la perspectiva TITULAR: desde la vista del tramitador
   * la carta la otorga la contraparte, no esta empresa.
   */
  titularId?: string;
  titularRazonSocial?: string;
  onVerDocumento: (documentoId: string) => void;
  onCambio: () => Promise<void> | void;
}) {
  const [filtro, setFiltro] = useState("");
  const [abierta, setAbierta] = useState<string | null>(null);
  const [autorizarEn, setAutorizarEn] = useState<RelacionTramitacion | null>(null);
  const [editar, setEditar] = useState<{ relacion: RelacionTramitacion; persona: PersonaAutorizada } | null>(null);
  const [cambiarEstatus, setCambiarEstatus] = useState<PersonaAutorizada | null>(null);
  const [agregando, setAgregando] = useState(false);

  const puedeAgregarTramitador = perspectiva === "TITULAR" && puedeAdministrar && !!titularId;

  // La contraparte es la otra punta de la relación: desde el tramitador se
  // consultan las empresas titulares atendidas; desde el titular, las gestorías.
  const contraparte = (r: RelacionTramitacion) =>
    perspectiva === "TRAMITADOR"
      ? { id: r.titularId, razonSocial: r.titularRazonSocial, rfc: r.titularRfc }
      : { id: r.tramitadorId, razonSocial: r.tramitadorRazonSocial, rfc: r.tramitadorRfc };

  const visibles = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return relaciones;
    return relaciones.filter((r) => {
      const c = contraparte(r);
      return (
        c.razonSocial.toLowerCase().includes(q) ||
        c.rfc.toLowerCase().includes(q) ||
        r.personas.some(
          (p) =>
            (p.nombreCompleto ?? "").toLowerCase().includes(q) ||
            (p.correo ?? "").toLowerCase().includes(q),
        )
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relaciones, filtro, perspectiva]);

  const vigentes = visibles.filter((r) => r.relacionVigente);
  const sinOperacion = visibles.filter((r) => !r.relacionVigente);

  // El botón vive fuera de la tabla porque también debe alcanzarse cuando la
  // empresa todavía no tiene ninguna relación: es justo el caso en el que hace
  // más falta.
  const botonAgregar = puedeAgregarTramitador ? (
    <button
      type="button"
      onClick={() => setAgregando(true)}
      className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
    >
      <Building2 className="h-4 w-4" />
      Agregar tramitador
    </button>
  ) : null;

  const modalAgregar =
    agregando && titularId ? (
      <AgregarTramitadorModal
        titularId={titularId}
        titularRazonSocial={titularRazonSocial ?? "esta empresa"}
        onClose={() => setAgregando(false)}
        onSaved={async () => {
          setAgregando(false);
          await onCambio();
        }}
      />
    ) : null;

  if (relaciones.length === 0) {
    return (
      <div className="space-y-2">
        <span className="block text-sm text-fg-faint">
          {perspectiva === "TRAMITADOR"
            ? "Esta razón social no tiene cartas poder que la autoricen a tramitar por otras empresas."
            : "Esta empresa no ha autorizado a ninguna empresa tramitadora."}
        </span>
        {botonAgregar}
        {modalAgregar}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint" />
          <input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder={
              perspectiva === "TRAMITADOR"
                ? "Filtra por empresa titular, RFC o persona autorizada"
                : "Filtra por empresa tramitadora, RFC o persona autorizada"
            }
            className="w-full rounded border bg-surface py-1.5 pl-8 pr-2 text-sm"
          />
        </div>
        <span className="whitespace-nowrap text-xs text-fg-subtle">
          {visibles.length} de {relaciones.length}
        </span>
        {botonAgregar}
      </div>

      {vigentes.length > 0 && (
        <TablaRelaciones
          filas={vigentes}
          perspectiva={perspectiva}
          abierta={abierta}
          setAbierta={setAbierta}
          puedeAdministrar={puedeAdministrar}
          onVerDocumento={onVerDocumento}
          onAutorizar={setAutorizarEn}
          onEditar={(relacion, persona) => setEditar({ relacion, persona })}
          onEstatus={setCambiarEstatus}
        />
      )}
      {vigentes.length === 0 && sinOperacion.length > 0 && (
        <p className="text-sm text-fg-faint">Ninguna relación opera hoy.</p>
      )}

      {/* Una carta poder no cambia de estatus porque su titular se retire: el
          instrumento existió y su histórico se conserva. Por eso estas
          relaciones no se borran, se separan — siguen vigentes en el papel,
          pero no operan. */}
      {sinOperacion.length > 0 && (
        <details className="rounded border bg-surface-2">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-fg-muted">
            Sin operación vigente ({sinOperacion.length})
            <span className="ml-2 font-normal text-xs text-fg-subtle">
              vigentes documentalmente, inactivas por el estatus de la razón social o de la carta
            </span>
          </summary>
          <div className="border-t p-2">
            <TablaRelaciones
              filas={sinOperacion}
              perspectiva={perspectiva}
              abierta={abierta}
              setAbierta={setAbierta}
              puedeAdministrar={false}
              onVerDocumento={onVerDocumento}
              onAutorizar={setAutorizarEn}
              onEditar={(relacion, persona) => setEditar({ relacion, persona })}
              onEstatus={setCambiarEstatus}
            />
          </div>
        </details>
      )}

      {modalAgregar}

      {autorizarEn && (
        <AutorizarPersonaModal
          relacion={autorizarEn}
          onClose={() => setAutorizarEn(null)}
          onSaved={async () => {
            setAutorizarEn(null);
            await onCambio();
          }}
        />
      )}

      {editar && (
        <EditarPersonaModal
          relacion={editar.relacion}
          persona={editar.persona}
          onClose={() => setEditar(null)}
          onSaved={async () => {
            setEditar(null);
            await onCambio();
          }}
        />
      )}

      <CambiarEstatusModal
        open={!!cambiarEstatus}
        title={
          cambiarEstatus
            ? `Estatus de la autorización de ${cambiarEstatus.nombreCompleto || cambiarEstatus.correo}`
            : ""
        }
        estatusActual={cambiarEstatus?.estatus ?? ""}
        opciones={[
          { value: "ACTIVA", label: "Activa", description: "La persona puede operar trámites de este titular." },
          {
            value: "SUSPENDIDA",
            label: "Inactiva",
            description: "Pausa reversible. No afecta sus autorizaciones con otras empresas.",
          },
          {
            value: "REVOCADA",
            label: "Revocada",
            description: "Terminal. Conserva el historial de esta relación para auditoría.",
          },
        ]}
        onCancel={() => setCambiarEstatus(null)}
        onConfirm={async (nuevoEstatus, motivo) => {
          await api.post(`/api/personas-autorizadas/${cambiarEstatus!.asociacionId}/estatus`, {
            estatus: nuevoEstatus,
            motivo,
          });
          setCambiarEstatus(null);
          await onCambio();
        }}
      />
    </div>
  );
}

function TablaRelaciones({
  filas,
  perspectiva,
  abierta,
  setAbierta,
  puedeAdministrar,
  onVerDocumento,
  onAutorizar,
  onEditar,
  onEstatus,
}: {
  filas: RelacionTramitacion[];
  perspectiva: "TITULAR" | "TRAMITADOR";
  abierta: string | null;
  setAbierta: (id: string | null) => void;
  puedeAdministrar: boolean;
  onVerDocumento: (documentoId: string) => void;
  onAutorizar: (r: RelacionTramitacion) => void;
  onEditar: (r: RelacionTramitacion, p: PersonaAutorizada) => void;
  onEstatus: (p: PersonaAutorizada) => void;
}) {
  const contraparte = (r: RelacionTramitacion) =>
    perspectiva === "TRAMITADOR"
      ? { razonSocial: r.titularRazonSocial, rfc: r.titularRfc }
      : { razonSocial: r.tramitadorRazonSocial, rfc: r.tramitadorRfc };

  return (
    <div className="overflow-x-auto rounded border bg-surface">
      <table className="w-full min-w-[46rem] text-sm">
        <thead className="bg-surface-2 text-left text-xs text-fg-muted">
          <tr>
            <th className="px-2 py-2 font-medium">
              {perspectiva === "TRAMITADOR" ? "Empresa titular" : "Empresa tramitadora"}
            </th>
            <th className="px-2 py-2 font-medium">RFC</th>
            <th className="px-2 py-2 font-medium">Representante legal</th>
            <th className="px-2 py-2 font-medium">Personal autorizado</th>
            <th className="px-2 py-2 font-medium">Instrumento</th>
            <th className="px-2 py-2 font-medium">Vigencia</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((r) => {
            const c = contraparte(r);
            const expandida = abierta === r.cartaPoderId;
            return (
              <Fragment key={r.cartaPoderId}>
                <tr
                  onClick={() => setAbierta(expandida ? null : r.cartaPoderId)}
                  className="cursor-pointer border-t align-top transition hover:bg-surface-2"
                >
                  <td className="px-2 py-2">
                    <span className="flex items-start gap-1 font-medium text-fg">
                      {expandida ? (
                        <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-faint" />
                      ) : (
                        <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-faint" />
                      )}
                      {c.razonSocial}
                    </span>
                    {r.motivoNoVigente && (
                      <span className="mt-0.5 block text-[11px] text-estado-warn">
                        {r.motivoNoVigente}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 font-mono text-xs text-fg-muted">{c.rfc}</td>
                  <td className="px-2 py-2 text-xs text-fg-muted">
                    {r.representantesContraparte.length === 0 ? (
                      <span className="text-estado-warn">Sin representante vigente</span>
                    ) : (
                      r.representantesContraparte.map((rep) => (
                        <span key={rep.id} className="block">
                          {rep.nombreCompleto}
                        </span>
                      ))
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {r.totalPersonasActivas === 0 ? (
                      <span className="text-xs text-estado-warn">Sin personal autorizado</span>
                    ) : (
                      <span className="text-xs text-fg-muted">
                        {r.totalPersonasActivas} persona{r.totalPersonasActivas === 1 ? "" : "s"}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {r.documentoId ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onVerDocumento(r.documentoId!);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong/40 px-2 py-1 text-xs text-primary-strong transition hover:bg-primary-tint"
                      >
                        <Eye className="h-3 w-3" /> Ver / descargar
                      </button>
                    ) : (
                      <StatusBadge tone="warn">Sin documento</StatusBadge>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs text-fg-muted">
                    {vigenciaTexto(r)}
                    {!r.cartaVigente && (
                      <span className="mt-0.5 block">
                        <StatusBadge tone="warn">Carta {r.estatusCarta.toLowerCase()}</StatusBadge>
                      </span>
                    )}
                  </td>
                </tr>

                {expandida && (
                  <tr className="border-t bg-surface-2">
                    <td colSpan={6} className="px-3 py-3">
                      <DetalleRelacion
                        relacion={r}
                        puedeAdministrar={puedeAdministrar}
                        onAutorizar={() => onAutorizar(r)}
                        onEditar={(p) => onEditar(r, p)}
                        onEstatus={onEstatus}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Nivel 3 del modelo: quién del tramitador opera dentro de ESTA relación (§5). */
function DetalleRelacion({
  relacion,
  puedeAdministrar,
  onAutorizar,
  onEditar,
  onEstatus,
}: {
  relacion: RelacionTramitacion;
  puedeAdministrar: boolean;
  onAutorizar: () => void;
  onEditar: (p: PersonaAutorizada) => void;
  onEstatus: (p: PersonaAutorizada) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-fg-muted">
          <span className="font-medium text-fg">{relacion.titularRazonSocial}</span>
          <span className="mx-1.5 text-fg-faint">→</span>
          <span className="font-medium text-fg">{relacion.tramitadorRazonSocial}</span>
          <Link
            to={`/cartas-poder/${relacion.cartaPoderId}`}
            className="ml-2 text-primary-strong hover:underline"
          >
            Ver carta poder
          </Link>
        </div>
        {puedeAdministrar && relacion.relacionVigente && (
          <button
            onClick={onAutorizar}
            className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint"
          >
            <UserPlus className="h-3.5 w-3.5" /> Autorizar persona
          </button>
        )}
      </div>

      {relacion.personas.length === 0 ? (
        <p className="text-xs text-fg-faint">
          Nadie del tramitador está autorizado en esta relación todavía.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {relacion.personas.map((p) => {
            const activa = p.estatus === "ACTIVA";
            const terminal = p.estatus === "REVOCADA" || p.estatus === "SUSTITUIDA";
            return (
              <li
                key={p.asociacionId}
                className="flex flex-wrap items-center gap-2 rounded border bg-surface p-2"
              >
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-fg">{p.nombreCompleto || p.correo}</span>
                  {p.correo && <span className="ml-2 text-xs text-fg-subtle">{p.correo}</span>}
                  <span className="block text-xs text-fg-subtle">
                    {TIPO_AUTORIZACION_LABEL[p.tipoAutorizacion] ?? p.tipoAutorizacion}
                    {p.puestoArea && ` · ${p.puestoArea}`}
                    {" · Sectores: "}
                    {p.sectoresCodigos.length > 0 ? p.sectoresCodigos.join(", ") : "—"}
                    {" · Vigencia: "}
                    {p.vigenciaFin ? `hasta ${formatDate(p.vigenciaFin)}` : "sin vencimiento"}
                  </span>
                </div>
                <StatusBadge tone={activa ? "ok" : terminal ? "warn" : "neutral"}>
                  {ESTATUS_PERSONA_LABEL[p.estatus] ?? p.estatus}
                </StatusBadge>
                {puedeAdministrar && !terminal && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => onEditar(p)}
                      className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong/40 px-2 py-1 text-xs text-primary-strong transition hover:bg-primary-tint"
                    >
                      <Pencil className="h-3 w-3" /> Editar
                    </button>
                    <button
                      onClick={() => onEstatus(p)}
                      className="inline-flex cursor-pointer items-center gap-1 rounded border border-estado-warn/25 px-2 py-1 text-xs text-estado-warn transition hover:bg-estado-warn-bg"
                      title="Inactivar, reactivar o revocar esta autorización"
                    >
                      <PowerOff className="h-3 w-3" /> Estatus
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface PersonalDelTramitador {
  usuarioId: string;
  nombreCompleto: string;
  correo: string | null;
  relacionConTramitador: string;
  yaAutorizadoEnEstaCarta: boolean;
}

/**
 * Otorga una carta poder nueva a una empresa tramitadora ya registrada.
 *
 * <p>Este es el camino para que una empresa sume una segunda gestoría sin
 * duplicarse a sí misma: la empresa (y su RFC) siguen siendo una sola fila en
 * `registro_legal`; lo que se agrega es la RELACIÓN, que vive en `carta_poder`.
 * Cada carta lleva después su propio documento, sus personas autorizadas y sus
 * facultades, sin tocar las relaciones que ya existían.
 *
 * <p>La vigencia se captura acotada a propósito. Una carta sin vencimiento
 * exige motivo y un autorizador distinto del capturista (segregación de
 * funciones), y eso se administra desde la pantalla de la carta, no aquí.
 */
function AgregarTramitadorModal({
  titularId,
  titularRazonSocial,
  onClose,
  onSaved,
}: {
  titularId: string;
  titularRazonSocial: string;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const hoy = new Date();
  const enUnAnio = new Date(hoy);
  enUnAnio.setFullYear(enUnAnio.getFullYear() + 1);
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const [tramitador, setTramitador] = useState<{
    id: string;
    razonSocial: string;
    rfc: string;
    estatus: string;
  } | null>(null);
  const [inicio, setInicio] = useState(iso(hoy));
  const [fin, setFin] = useState(iso(enUnAnio));
  const [copiaCertificada, setCopiaCertificada] = useState(false);
  const [descripcion, setDescripcion] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      await api.post(`/api/cartas-poder`, {
        registroLegalTitularId: titularId,
        registroLegalTramitadorId: tramitador!.id,
        vigenciaInicio: new Date(inicio + "T00:00:00").toISOString(),
        vigenciaFin: new Date(fin + "T23:59:59").toISOString(),
        sinVencimiento: false,
        esCopiaCertificadaOriginal: copiaCertificada,
        documentoDescripcion: descripcion.trim() || null,
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (e: unknown) => setErr(errorDetalle(e)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!tramitador) {
      setErr("Selecciona la empresa tramitadora que quedará autorizada.");
      return;
    }
    if (!inicio || !fin) {
      setErr("Captura el inicio y el fin de la vigencia de la carta poder.");
      return;
    }
    if (new Date(fin) <= new Date(inicio)) {
      setErr("La fecha de fin debe ser posterior a la de inicio.");
      return;
    }
    setErr(null);
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">Agregar tramitador a {titularRazonSocial}</h3>
        </div>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
            <p className="text-xs text-fg-subtle">
              Se creará una carta poder nueva. Las relaciones que esta empresa ya tenga se conservan
              intactas: una razón social puede tener varias gestorías autorizadas al mismo tiempo, cada
              una con sus propias personas, documentos y facultades.
            </p>

            {/*
              NO envolver el picker en un <label>: al elegir un renglón, el picker
              se re-renderiza mostrando el botón «X» de quitar, y la activación del
              label reenvía el clic a ese botón, que dispara onChange(null). La
              selección se ponía y se borraba en el mismo clic. La pantalla de alta
              lo mete en un <div> por la misma razón.
            */}
            <div className="text-sm">
              <span className="mb-1 block font-medium text-fg-muted">Empresa tramitadora</span>
              <TramitadorPicker value={tramitador} onChange={setTramitador} />
              <span className="mt-1 block text-xs text-fg-subtle">
                Solo aparecen razones sociales ya registradas con la bandera Tramitador y en estatus
                Aprobado o Activo.
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-fg-muted">Inicio de vigencia</span>
                <input
                  type="date"
                  value={inicio}
                  onChange={(e) => setInicio(e.target.value)}
                  className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-fg-muted">Fin de vigencia</span>
                <input
                  type="date"
                  value={fin}
                  onChange={(e) => setFin(e.target.value)}
                  className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
                />
              </label>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={copiaCertificada}
                onChange={(e) => setCopiaCertificada(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Es copia certificada u original
                <span className="mt-0.5 block text-xs text-fg-subtle">
                  Requisito para que la carta sea válida en el sector Telecomunicaciones (RL-RN-103).
                </span>
              </span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-fg-muted">
                Descripción del instrumento <span className="font-normal text-fg-subtle">(opcional)</span>
              </span>
              <input
                type="text"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ej. Escritura pública 12345, Notaría 7"
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              />
              <span className="mt-1 block text-xs text-fg-subtle">
                El archivo PDF se adjunta después, desde el detalle de la carta poder.
              </span>
            </label>

            {err && (
              <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{err}</span>
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t bg-surface px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="cursor-pointer rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mut.isPending ? "Guardando…" : "Agregar tramitador"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Alta de persona autorizada dentro de una relación. Solo ofrece personal ya
 * relacionado con la empresa tramitadora: el nivel 3 del modelo se apoya en el
 * nivel 2 (§5/§12), y así no se autoriza a cuentas ajenas a la gestoría.
 */
function AutorizarPersonaModal({
  relacion,
  onClose,
  onSaved,
}: {
  relacion: RelacionTramitacion;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [usuarioId, setUsuarioId] = useState("");
  const [tipo, setTipo] = useState("GESTION");
  const [sectoresSel, setSectoresSel] = useState<string[]>([]);
  const [puesto, setPuesto] = useState("");
  const [vigenciaFin, setVigenciaFin] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const personalQ = useQuery({
    queryKey: ["carta-poder", relacion.cartaPoderId, "personal-tramitador"],
    queryFn: async () =>
      (
        await api.get<PersonalDelTramitador[]>(
          `/api/cartas-poder/${relacion.cartaPoderId}/personal-tramitador`,
          { params: { tramitadorId: relacion.tramitadorId } },
        )
      ).data,
  });
  const sectoresQ = useQuery({
    queryKey: ["catalogo-sectores"],
    queryFn: async () => (await api.get<SectorOption[]>(`/api/catalogos/sectores`)).data,
  });

  const disponibles = (personalQ.data ?? []).filter((p) => !p.yaAutorizadoEnEstaCarta);

  const mut = useMutation({
    mutationFn: async () => {
      await api.post(`/api/cartas-poder/${relacion.cartaPoderId}/personas-autorizadas`, {
        usuarioId,
        tipoAutorizacion: tipo,
        sectoresIds: sectoresSel,
        puestoArea: puesto.trim() || null,
        vigenciaFin: vigenciaFin ? new Date(vigenciaFin + "T23:59:59").toISOString() : null,
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (e: unknown) => setErr(errorDetalle(e)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioId) {
      setErr("Selecciona a la persona del tramitador que quedará autorizada.");
      return;
    }
    if (sectoresSel.length === 0) {
      setErr("Selecciona al menos un sector (la autorización se otorga por sector).");
      return;
    }
    setErr(null);
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">
            Autorizar persona de {relacion.tramitadorRazonSocial}
          </h3>
        </div>
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            <p className="rounded-lg border border-estado-info/25 bg-estado-info-bg p-3 text-xs text-estado-info">
              La autorización vale solo para <strong>{relacion.titularRazonSocial}</strong>. Autorizar
              aquí no cambia ningún otro papel de la persona ni la autoriza para las demás empresas que
              atiende {relacion.tramitadorRazonSocial}.
            </p>

            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">
                Persona <span className="text-estado-err">*</span>
              </span>
              <select
                value={usuarioId}
                onChange={(e) => setUsuarioId(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              >
                <option value="">— Selecciona personal del tramitador —</option>
                {disponibles.map((p) => (
                  <option key={p.usuarioId} value={p.usuarioId}>
                    {p.nombreCompleto || p.correo} ({p.relacionConTramitador})
                  </option>
                ))}
              </select>
              {personalQ.isLoading ? (
                <span className="mt-1 block text-xs text-fg-subtle">Cargando personal…</span>
              ) : disponibles.length === 0 ? (
                <span className="mt-1 block text-xs text-estado-warn">
                  Todo el personal registrado de {relacion.tramitadorRazonSocial} ya está autorizado en
                  esta relación. Para sumar a alguien más, regístralo primero en el Registro Legal del
                  tramitador.
                </span>
              ) : null}
            </label>

            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">Tipo de autorización</span>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              >
                <option value="GESTION">Gestión (crear y operar solicitudes)</option>
                <option value="CONSULTA">Consulta (solo lectura)</option>
                <option value="SEGUIMIENTO">Seguimiento</option>
                <option value="FIRMA">Firma</option>
              </select>
            </label>

            <div>
              <span className="mb-1 block text-xs font-medium text-fg-muted">
                Sectores autorizados <span className="text-estado-err">*</span>
              </span>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {(sectoresQ.data ?? []).map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm hover:bg-surface-2"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={sectoresSel.includes(s.id)}
                      onChange={() =>
                        setSectoresSel(
                          sectoresSel.includes(s.id)
                            ? sectoresSel.filter((x) => x !== s.id)
                            : [...sectoresSel, s.id],
                        )
                      }
                    />
                    {s.nombre}
                  </label>
                ))}
              </div>
            </div>

            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">
                Puesto o área en el tramitador
              </span>
              <input
                value={puesto}
                onChange={(e) => setPuesto(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
                placeholder="p. ej. Coordinador de certificaciones"
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">
                Vigencia de la autorización (fin)
              </span>
              <input
                type="date"
                value={vigenciaFin}
                onChange={(e) => setVigenciaFin(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              />
              <span className="mt-1 block text-xs text-fg-subtle">
                Vacío = hereda la vigencia de la carta poder ({vigenciaTexto(relacion).toLowerCase()}).
              </span>
            </label>

            {err && (
              <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{err}</span>
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t bg-surface px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="cursor-pointer rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mut.isPending ? "Guardando…" : "Autorizar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Edición del alcance de una persona dentro de la relación (facultad, sectores, puesto, vigencia). */
function EditarPersonaModal({
  relacion,
  persona,
  onClose,
  onSaved,
}: {
  relacion: RelacionTramitacion;
  persona: PersonaAutorizada;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [tipo, setTipo] = useState(persona.tipoAutorizacion);
  const [sectoresSel, setSectoresSel] = useState<string[] | null>(null); // null = sin cambios
  const [puesto, setPuesto] = useState(persona.puestoArea ?? "");
  const [vigenciaFin, setVigenciaFin] = useState(
    persona.vigenciaFin ? persona.vigenciaFin.slice(0, 10) : "",
  );
  const [motivo, setMotivo] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const sectoresQ = useQuery({
    queryKey: ["catalogo-sectores"],
    queryFn: async () => (await api.get<SectorOption[]>(`/api/catalogos/sectores`)).data,
  });
  const sectoresActualesIds = (sectoresQ.data ?? [])
    .filter((s) => persona.sectoresCodigos.includes(s.codigo))
    .map((s) => s.id);
  const seleccion = sectoresSel ?? sectoresActualesIds;

  const mut = useMutation({
    mutationFn: async () => {
      await api.patch(`/api/personas-autorizadas/${persona.asociacionId}`, {
        motivo: motivo.trim(),
        tipoAutorizacion: tipo !== persona.tipoAutorizacion ? tipo : null,
        sectoresIds: sectoresSel,
        puestoArea: puesto,
        actualizarVigenciaFin: true,
        vigenciaFin: vigenciaFin ? new Date(vigenciaFin + "T23:59:59").toISOString() : null,
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (e: unknown) => setErr(errorDetalle(e)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!motivo.trim()) {
      setErr("El motivo del cambio es obligatorio (auditable).");
      return;
    }
    if (seleccion.length === 0) {
      setErr("La autorización requiere al menos un sector; para retirar el acceso usa Estatus.");
      return;
    }
    setErr(null);
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">
            Autorización de {persona.nombreCompleto || persona.correo} para {relacion.titularRazonSocial}
          </h3>
        </div>
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">Tipo de autorización</span>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              >
                <option value="GESTION">Gestión (crear y operar solicitudes)</option>
                <option value="CONSULTA">Consulta (solo lectura)</option>
                <option value="SEGUIMIENTO">Seguimiento</option>
                <option value="FIRMA">Firma</option>
              </select>
            </label>

            <div>
              <span className="mb-1 block text-xs font-medium text-fg-muted">
                Sectores autorizados <span className="text-estado-err">*</span>
              </span>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {(sectoresQ.data ?? []).map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm hover:bg-surface-2"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={seleccion.includes(s.id)}
                      onChange={() =>
                        setSectoresSel(
                          seleccion.includes(s.id)
                            ? seleccion.filter((x) => x !== s.id)
                            : [...seleccion, s.id],
                        )
                      }
                    />
                    {s.nombre}
                  </label>
                ))}
              </div>
            </div>

            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">Puesto o área</span>
              <input
                value={puesto}
                onChange={(e) => setPuesto(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">
                Vigencia de la autorización (fin)
              </span>
              <input
                type="date"
                value={vigenciaFin}
                onChange={(e) => setVigenciaFin(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              />
              <span className="mt-1 block text-xs text-fg-subtle">
                Vacío = hereda la vigencia de la carta poder ({vigenciaTexto(relacion).toLowerCase()}).
              </span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">
                Motivo del cambio <span className="text-estado-err">*</span>
              </span>
              <input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
                placeholder="Queda asentado en la bitácora"
              />
            </label>

            {err && (
              <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{err}</span>
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t bg-surface px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="cursor-pointer rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mut.isPending ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Hook de datos del panel: una sola llamada trae los dos sentidos de la relación. */
export function useRelacionesTramitacion(registroLegalId: string | undefined) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["registro-legal", registroLegalId, "relaciones-tramitacion"],
    queryFn: async () =>
      (
        await api.get<RelacionesTramitacion>(
          `/api/registros-legales/${registroLegalId}/relaciones-tramitacion`,
        )
      ).data,
    enabled: !!registroLegalId,
  });
  const invalidar = () =>
    qc.invalidateQueries({
      queryKey: ["registro-legal", registroLegalId, "relaciones-tramitacion"],
    });
  return { ...query, invalidar };
}
